/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../Css/AboutSafpro.css';

export default function SafproAbout() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);
  const [cornerParticles, setCornerParticles] = useState([]);
  const buttonRef = useRef(null);

  const generateParticles = (count, x, y) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Math.random(),
        x: x || Math.random() * 100,
        y: y || Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 1 + 0.5,
        delay: Math.random() * 0.5
      });
    }
    return newParticles;
  };

  const generateCornerParticles = (count, buttonRect) => {
    if (!buttonRect) return [];
    
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      // Start from the bottom-right corner where the cut is
      const startX = buttonRect.right - 8;
      const startY = buttonRect.bottom - 8;
      
      // Create a downward and outward flow from the bottom-right corner
      const angle = Math.random() * Math.PI / 3 + Math.PI / 6; // 30-90 degrees
      const distance = 80 + Math.random() * 200;
      
      const endX = startX + Math.cos(angle) * distance;
      const endY = startY + Math.sin(angle) * distance;
      
      // Curved path points
      const midX1 = startX + Math.cos(angle) * distance * 0.2;
      const midY1 = startY + Math.sin(angle) * distance * 0.2;
      const midX2 = startX + Math.cos(angle) * distance * 0.5;
      const midY2 = startY + Math.sin(angle) * distance * 0.5;
      const midX3 = startX + Math.cos(angle) * distance * 0.8;
      const midY3 = startY + Math.sin(angle) * distance * 0.8;
      
      const speedTypes = ['slow', 'medium', 'fast'];
      const speed = speedTypes[Math.floor(Math.random() * speedTypes.length)];
      
      const shapeTypes = ['circle', 'blob', 'drop'];
      const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
      
      // Use only the violet colors from the button
      const baseColors = [
        'rgba(72, 52, 212, 0.9)',  // #4834D4
        'rgba(104, 109, 224, 0.8)', // #686DE0
        'rgba(91, 33, 182, 0.7)',   // #5b21b6
      ];
      const color = baseColors[Math.floor(Math.random() * baseColors.length)];
      
      newParticles.push({
        id: Math.random(),
        startX: `${startX}px`,
        startY: `${startY}px`,
        endX: `${endX}px`,
        endY: `${endY}px`,
        midX1: `${midX1}px`,
        midY1: `${midY1}px`,
        midX2: `${midX2}px`,
        midY2: `${midY2}px`,
        midX3: `${midX3}px`,
        midY3: `${midY3}px`,
        size: Math.random() * 5 + 2,
        color: color,
        speed: speed,
        shape: shape,
        opacity: Math.random() * 0.7 + 0.3
      });
    }
    return newParticles;
  };

  const handleMouseEnter = (e) => {
    // Prevent event bubbling when using Link
    e.preventDefault();
    setIsHovered(true);
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setParticles(generateParticles(8, 50, 50));
      setCornerParticles(generateCornerParticles(6, buttonRect));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      setParticles([]);
      setTimeout(() => setCornerParticles([]), 1000);
    }, 500);
  };

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      
      // Regular particles interval
      const particleInterval = setInterval(() => {
        setParticles(prev => [...prev, ...generateParticles(2)]);
      }, 200);
      
      // Corner particles interval
      const cornerParticleInterval = setInterval(() => {
        setCornerParticles(prev => {
          const updatedParticles = prev.filter(p => 
            Date.now() - p.createdAt < 4000
          );
          
          const newParticles = generateCornerParticles(2, buttonRect).map(p => ({
            ...p,
            createdAt: Date.now()
          }));
          
          return [...updatedParticles, ...newParticles];
        });
      }, 300);
      
      return () => {
        clearInterval(particleInterval);
        clearInterval(cornerParticleInterval);
      };
    }
  }, [isHovered]);

  // Update particle positions on scroll/resize
  useEffect(() => {
    const updateParticles = () => {
      if (isHovered && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setCornerParticles(prev => 
          prev.map(particle => ({
            ...particle,
            startX: `${buttonRect.right - 8}px`,
            startY: `${buttonRect.bottom - 8}px`
          }))
        );
      }
    };

    window.addEventListener('scroll', updateParticles);
    window.addEventListener('resize', updateParticles);

    return () => {
      window.removeEventListener('scroll', updateParticles);
      window.removeEventListener('resize', updateParticles);
    };
  }, [isHovered]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <div className="py-5 my-5 overflow-hidden" ref={sectionRef}>
         {cornerParticles.map(particle => (
          <div
            key={particle.id}
            className={`bottom-corner-leak-particle bottom-corner-leak-particle--${particle.speed} bottom-corner-leak-particle--${particle.shape}`}
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: particle.color,
              left: 0,
              top: 0,
              '--start-x': particle.startX,
              '--start-y': particle.startY,
              '--mid-x1': particle.midX1,
              '--mid-y1': particle.midY1,
              '--mid-x2': particle.midX2,
              '--mid-y2': particle.midY2,
              '--mid-x3': particle.midX3,
              '--mid-y3': particle.midY3,
              '--end-x': particle.endX,
              '--end-y': particle.endY,
              opacity: particle.opacity
            }}
          />
        ))}
        <div className="container-fluid container-lg px-3 px-md-4">
          {/* Header */}
          <div className="mb-3 mb-md-4">
            <button className="btn btn-about px-3 px-md-4 py-2 border-1 fw-semibold">
              ABOUT SAFPRO
            </button>
          </div>

          <div className="row g-3 g-md-4 g-lg-5 align-items-center">
            {/* Right Content - Slides from Right - Shows First on Mobile */}
            <div className={`col-lg-6 order-1 order-lg-2 slide-in-right ${isVisible ? 'visible' : ''}`}>
              <div className="person-container">
                {/* Main person image */}
                <img src="/images/Man.png" alt="Person" className="person-image" />
                
                {/* Overlay images */}
                <img src="/images/Stars.png" alt="Stars" className="overlay-image stars-img" />
                {/* <img src="/images/projects.png" alt="Projects" className="overlay-image projects-img" /> */}
                {/* <img src="/images/Growth.png" alt="Growth" className="overlay-image growth-img" /> */}
              </div>
            </div>

            {/* Left Content - Slides from Left - Shows Second on Mobile */}
            <div className={`col-lg-6 order-2 order-lg-1 slide-in-left ${isVisible ? 'visible' : ''}`}>
              <h1 className="display-6 fw-bold text-dark mb-3 mb-md-4">
                SAFPRO TECHNOLOGY SOLUTIONS
              </h1>

              <p className="text-secondary fs-6 mb-3 mb-md-4 lh-lg">
                We are an offshore software development company. Our expertise encompasses a wide range of cutting-edge technologies, including web applications, mobile apps, e-commerce, digital websites, custom portal development, UI/UX design, digital marketing, software testing, DevOps, AI/ML, Salesforce, and cloud services.
              </p>

              <p className="text-secondary fs-6 mb-3 mb-md-4 lh-lg">
                Our team of experts are well-versed in the latest technologies and are dedicated to delivering high-quality software solutions that meets the customer requirement and satisfaction. We develop from scratch end to end solutions and also we do with existing open source platform as per the clients needs.
              </p>

              {/* <p className="text-secondary fs-6 mb-3 mb-md-4 lh-lg">
                Dedicated team at safpro provides the necessary resources companies need to extend their technology teams. Safpro has maintained a 93% customers retention rate and success of our partners. We are a leader in efficient cost high quality solutions due to our dedication to our services, optimized process and our hybrid outsourcing business model.
              </p> */}

              <Link to="/about-safproview" style={{ textDecoration: 'none', display: 'inline-block' }}>
                <button
                  ref={buttonRef}
                  className={`modern-btn ${isHovered ? 'modern-btn--hovered' : ''}`}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="modern-btn__text">Know More<FontAwesomeIcon className='mx-2' icon={faArrowUpRightFromSquare}/> </span>
                  

                  <span className="modern-btn__border modern-btn__border--top"></span>
                  <span className="modern-btn__border modern-btn__border--right"></span>
                  <span className="modern-btn__border modern-btn__border--bottom"></span>
                  <span className="modern-btn__border modern-btn__border--left"></span>

                  <div className="modern-btn__particles">
                    {particles.map(particle => (
                      <span
                        key={particle.id}
                        className="modern-btn__particle"
                        style={{
                          left: `${particle.x}%`,
                          top: `${particle.y}%`,
                          width: `${particle.size}px`,
                          height: `${particle.size}px`,
                          animationDuration: `${particle.duration}s`,
                          animationDelay: `${particle.delay}s`
                        }}
                      />
                    ))}
                  </div>
                  

                  <span className="modern-btn__glow"></span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}